import React, { useEffect, useState } from "react";
import "./contact.css";
import Navbar from "../../common/navBar/navBar.tsx";
import { submitContact } from "../../../services/contactService.js";
import { validateContactForm } from "../../../validations/contactValidationSchema.ts";
import Footer from "../../common/footer/footer.tsx";

const Contact = ({ showNavbar = true }: { showNavbar?: boolean }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    status: "Unread",
  });

  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    subject?: string;
    message?: string;
    status?: string;
  }>({});

  const [status, setStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({
    type: null,
    message: "",
  });

  const [loading, setLoading] = useState(false);

  // ── Handle Input ─────────────────────
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    // clear error while typing
    if (errors[name as keyof typeof errors]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // ── Auto hide status ──────────────────
  useEffect(() => {
    if (status.type) {
      const timer = setTimeout(() => {
        setStatus({ type: null, message: "" });
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [status]);

  // ── Submit ────────────────────────────
  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const validationErrors = validateContactForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      setStatus({ type: null, message: "" });

      const response = await submitContact(formData);

      setStatus({
        type: "success",
        message: response.message || "Message sent successfully!",
      });

      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        status: "Unread",
      });

      setErrors({});
    } catch (error: any) {
      setStatus({
        type: "error",
        message: error.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showNavbar && <Navbar />}

      <div className='contact-page'>
        {/* 🔥 LEFT SIDE - COMPLETELY UNCHANGED */}
        <div className='contact-hero'>
          <h2 className='contact-hero_title'>Contact Us</h2>
          <p className='contact-hero__subtitle'>
            Where perfect matches begin. We're committed to your safety, with advanced measures in place to keep our
            community secure and reliable.
          </p>
        </div>

        <div className='contact-content'>
          {/* LEFT SIDE (UNCHANGED) */}
          <div className='contact-info'>
            <h2 className='contact-info__heading'>Get In Touch</h2>
            <p className='contact-info__description'>
              Curious to learn more about your journey to finding the perfect match? Have questions about our membership
              plans? Reach out to us via email—we're here to guide you every step of the way.
            </p>

            <div className='contact-info__cards'>
              <div className='contact-card'>
                <div className='contact-card__icon'>
                  <svg viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z'
                      stroke='white'
                      strokeWidth='2'
                    />
                    <polyline points='22,6 12,13 2,6' stroke='white' strokeWidth='2' />
                  </svg>
                </div>
                <div className='contact-card__text'>
                  <span className='contact-card__label'>Email Address</span>
                  <span className='contact-card__value'>svenugoban1996@gmail.com</span>
                </div>
              </div>

              <div className='contact-card'>
                <div className='contact-card__icon'>
                  <svg viewBox='0 0 24 24' fill='none'>
                    <circle cx='12' cy='12' r='10' stroke='white' strokeWidth='2' />
                    <polyline points='12,6 12,12 16,14' stroke='white' strokeWidth='2' />
                  </svg>
                </div>
                <div className='contact-card__text'>
                  <span className='contact-card__label'>Response Time</span>
                  <span className='contact-card__value'>We respond within 24–48 hours</span>
                </div>
              </div>

              <div className='contact-card'>
                <div className='contact-card__icon'>
                  <svg viewBox='0 0 24 24' fill='none'>
                    <rect x='3' y='4' width='18' height='18' rx='2' stroke='white' />
                    <line x1='16' y1='2' x2='16' y2='6' stroke='white' />
                    <line x1='8' y1='2' x2='8' y2='6' stroke='white' />
                    <line x1='3' y1='10' x2='21' y2='10' stroke='white' />
                  </svg>
                </div>
                <div className='contact-card__text'>
                  <span className='contact-card__label'>Call Us</span>
                  <span className='contact-card__value'>+94 753440728</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE (UPDATED ONLY) */}
          <div className='contact-form-card'>
            <h2 className='contact-form__heading'>Send us a Message</h2>

            {/* Name */}
            <div className='contact-form__group'>
              <label className='contact-form__label'>Your Name</label>
              <input
                type='text'
                name='name'
                className='contact-form__input'
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <span className='contact-form__error'>{errors.name}</span>}
            </div>

            {/* Email */}
            <div className='contact-form__group'>
              <label className='contact-form__label'>Email Address</label>
              <input
                type='email'
                name='email'
                className='contact-form__input'
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <span className='contact-form__error'>{errors.email}</span>}
            </div>

            {/* Subject */}
            <div className='contact-form__group'>
              <label className='contact-form__label'>Subject</label>
              <input
                type='text'
                name='subject'
                className='contact-form__input'
                value={formData.subject}
                onChange={handleChange}
              />
              {errors.subject && <span className='contact-form__error'>{errors.subject}</span>}
            </div>

            {/* Message */}
            <div className='contact-form__group'>
              <label className='contact-form__label'>Your Message</label>
              <textarea
                name='message'
                className='contact-form__textarea'
                rows={5}
                value={formData.message}
                onChange={handleChange}
              />
              {errors.message && <span className='contact-form__error'>{errors.message}</span>}
            </div>

            {/* Status */}
            {status.type && <div className={`contact-form__status ${status.type}`}>{status.message}</div>}

            {/* Button */}
            <button className='contact-form__submit' onClick={handleSubmit} disabled={loading}>
              {loading ? "Sending..." : "Send Message"}
            </button>
          </div>
        </div>
      </div>
      {showNavbar && <Footer />}
    </>
  );
};

export default Contact;
